
import {
  Component,
  OnInit,
  OnDestroy
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';
import { LoginService } from '../../../../components/Authentication/auth/login.service';
import { LogoutService } from '../../../../components/Authentication/auth/logout.service';
import { AccountTypeService } from '../../../../services/account-type';
import { i18Service } from '../../../../services/i18service';


interface MenuItem {
  label: string;
  icon: string;
  route: string;
  accessPage?: string;
  alwaysVisible?: boolean;
  requiresRoleCheck?: () => boolean;
}

@Component({
  selector: 'app-shopandclinic-m',
  templateUrl: './shopandclinic.m.component.html',
  styleUrl: './shopandclinic.m.component.scss'
})
export class ShopandclinicMComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  tokenExists = false;
  openMenu = false;
  cartPopUp = false;
  showCart = true;

  cartItems: any[] = [];
  cartItemCount = 0;

  constructor(
    private loginService: LoginService,
    private logoutService: LogoutService,
    private accountService: AccountTypeService,
    private router: Router,
    private location: Location,
    private i18: i18Service
  ) {
    this.i18.init();
  }

  /* ================= MENU CONFIG ================= */

  private menuConfig: MenuItem[] = [

    { label: 'Pospage', icon: 'Home.png', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'Stock', icon: 'stock.png', route: '/Home.mobile', accessPage: '6' },
    { label: 'clinic', icon: 'clinic.png', route: '/Clinic.mobile', accessPage: '2' },
    { label: 'Orders', icon: 'add.png', route: '/orders.mobile', accessPage: '5' },
    { label: 'delivery', icon: 'ontheway.png', route: '/deliverymanager.mobile', accessPage: '3' },
    { label: 'supplies', icon: 'sup.png', route: '/suppliespage.mobile', accessPage: '4' },
    { label: 'myclients', icon: 'phone.png', route: '/myclients.mobile', accessPage: '2' },
    { label: 'financial', icon: 'card photo.png', route: '/dailyexpenses', accessPage: '1' },
    { label: 'employees', icon: 'emploees.png', route: '/employeepage.mobile', accessPage: '0' },

    {
      label: 'Users',
      icon: 'profile.png',
      route: '/allusers.mobile',
      requiresRoleCheck: () => this.accountService.canManageUsers()
    },

    { label: 'Profile', icon: 'profile.png', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fingerprint.png', route: '/fingerprint.mobile', alwaysVisible: true }

  ];

  get menuItems(): MenuItem[] {
    return this.menuConfig.filter(item => {

      if (item.requiresRoleCheck) {
        return item.requiresRoleCheck();
      }

      if (item.alwaysVisible) return true;

      if (!item.accessPage) return false;

      return this.accountService.hasAccess(item.accessPage);
    });
  }

  /* ================= LIFECYCLE ================= */

  ngOnInit(): void {

    this.loginService.token$
      .pipe(takeUntil(this.destroy$))
      .subscribe(token => {
        this.tokenExists = !!token;
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateCart();
        this.updateCartVisibility();
        this.cartPopUp = false;
      });

    this.updateCart();
    this.updateCartVisibility();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ================= UI ================= */

  openMenuToggle() {
    this.openMenu = !this.openMenu;
  }

  close() {
    this.openMenu = false;
    this.cartPopUp = false;
  }

  toggleLanguage() {
    this.i18.toggleLanguage();
    this.close();
  }

  logout() {
    this.logoutService.logout(localStorage.getItem('token'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        localStorage.clear();
        window.location.reload();
      });
  }

  /* ================= CART ================= */

  updateCart() {
    this.cartItems = this.readCartForCurrentWarehouse();
    this.cartItemCount = this.cartItems.length;
  }

  private readCartForCurrentWarehouse(): any[] {
    const currentWarehouseId = localStorage.getItem('posId');
    const cartWarehouseId = localStorage.getItem('cartWarehouseId');

    if (cartWarehouseId && currentWarehouseId && cartWarehouseId !== currentWarehouseId) {
      this.clearCartStorage();
      return [];
    }

    const storedCart = localStorage.getItem('cart');

    try {
      return storedCart ? JSON.parse(storedCart) : [];
    } catch {
      this.clearCartStorage();
      return [];
    }
  }

  private saveCartStorage(): void {
    if (!this.cartItems.length) {
      this.clearCartStorage();
      return;
    }

    const currentWarehouseId = localStorage.getItem('posId');
    localStorage.setItem('cart', JSON.stringify(this.cartItems));

    if (currentWarehouseId) {
      localStorage.setItem('cartWarehouseId', currentWarehouseId);
    }
  }

  private clearCartStorage(): void {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartWarehouseId');
  }

  updateCartVisibility() {
    const path = this.location.path();
    this.showCart =
      !path.includes('/Addorder') &&
      !path.includes('/Home.mobile');
  }

  cartPopUpOpen() {
    this.cartPopUp = true;
  }

  closeCart() {
    this.cartPopUp = false;
  }

  removeFromCart(item: any) {
    this.cartItems = this.cartItems.filter(i => i.id !== item.id);
    this.saveCartStorage();
    this.updateCart();
  }

  quickOrder() {
    this.router.navigate(['/Addorder'], {
      queryParams: { cart: JSON.stringify(this.cartItems) }
    });
  }

}
